import client from './client'
import type {
  CommandHistoryListResponse,
  CommandResponse,
  DashboardStats,
  Device,
  DeviceListResponse,
  ForceWipeResponse,
  Geolocation,
  GeolocationListResponse,
  LockDeviceRequest,
  Ping,
  PingListResponse,
  SendMessageRequest,
  UnlockDeviceRequest,
  WipeDeviceRequest,
} from './device.types'

interface BffEnvelope<T> {
  code: string
  message: string
  data: T
}

function unwrap<T>(response: { data: BffEnvelope<T> }): T {
  return response.data.data
}

// -------- Devices --------
export async function listDevices(params?: {
  limit?: number
  cursor?: string
  direction?: 'next' | 'previous'
  search?: string
}): Promise<DeviceListResponse> {
  const resp = await client.get<BffEnvelope<DeviceListResponse>>('/v1/devices', { params })
  return unwrap(resp)
}

export async function getDevice(deviceId: string): Promise<Device> {
  const resp = await client.get<BffEnvelope<Device>>(`/v1/devices/${deviceId}`)
  return unwrap(resp)
}

export async function getDevicePings(deviceId: string, params?: {
  limit?: number
  cursor?: string
  direction?: 'next' | 'previous'
}): Promise<PingListResponse> {
  const resp = await client.get<BffEnvelope<PingListResponse>>(`/v1/devices/${deviceId}/pings`, { params })
  return unwrap(resp)
}

export async function forceWipeDevice(deviceId: string): Promise<ForceWipeResponse> {
  const resp = await client.post<BffEnvelope<ForceWipeResponse>>(`/v1/devices/${deviceId}/force-wipe`)
  return unwrap(resp)
}

// -------- Commands --------
export async function lockDevice(deviceId: string, body: LockDeviceRequest): Promise<CommandResponse> {
  const resp = await client.post<BffEnvelope<CommandResponse>>(`/v1/devices/${deviceId}/lock`, body)
  return unwrap(resp)
}

export async function unlockDevice(deviceId: string, body?: UnlockDeviceRequest): Promise<CommandResponse> {
  const resp = await client.post<BffEnvelope<CommandResponse>>(`/v1/devices/${deviceId}/unlock`, body ?? {})
  return unwrap(resp)
}

export async function sendDeviceMessage(deviceId: string, body: SendMessageRequest): Promise<CommandResponse> {
  const resp = await client.post<BffEnvelope<CommandResponse>>(`/v1/devices/${deviceId}/message`, body)
  return unwrap(resp)
}

export async function wipeDevice(deviceId: string, body?: WipeDeviceRequest): Promise<CommandResponse> {
  const resp = await client.post<BffEnvelope<CommandResponse>>(`/v1/devices/${deviceId}/wipe`, body ?? {})
  return unwrap(resp)
}

export async function resendCommand(commandId: string): Promise<CommandResponse> {
  const resp = await client.post<BffEnvelope<CommandResponse>>(`/v1/commands/history/${commandId}/resend`)
  return unwrap(resp)
}

export async function listCommandHistory(params?: {
  cursor?: string
  limit?: number
  deviceId?: string
  status?: string
  dir?: 'next' | 'prev'
}): Promise<CommandHistoryListResponse> {
  const resp = await client.get<BffEnvelope<CommandHistoryListResponse>>('/v1/commands/history', { params })
  return unwrap(resp)
}

// -------- Pings --------
export async function listPings(params?: {
  limit?: number
  cursor?: string
  direction?: 'next' | 'previous'
  search?: string
}): Promise<PingListResponse> {
  const resp = await client.get<BffEnvelope<PingListResponse>>('/v1/pings', { params })
  return unwrap(resp)
}

export async function getPing(pingId: number): Promise<Ping> {
  const resp = await client.get<BffEnvelope<Ping>>(`/v1/pings/${pingId}`)
  return unwrap(resp)
}

// -------- Dashboard --------
export async function getDashboardStats(): Promise<DashboardStats> {
  const resp = await client.get<BffEnvelope<DashboardStats>>('/v1/dashboard/stats')
  return unwrap(resp)
}

// -------- Geolocations --------
export async function listGeolocations(params?: { limit?: number; offset?: number }): Promise<GeolocationListResponse> {
  const resp = await client.get<BffEnvelope<GeolocationListResponse>>('/v1/geolocations', { params })
  return unwrap(resp)
}

export async function getGeolocation(ipAddress: string): Promise<Geolocation> {
  const resp = await client.get<BffEnvelope<Geolocation>>(`/v1/geolocations/${ipAddress}`)
  return unwrap(resp)
}
